import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  Search, Filter, Download, RefreshCw, AlertTriangle, 
  AlertCircle, Info, Shield, Users, Building2, FileText, 
  DollarSign, Clock, ChevronLeft, ChevronRight, X, Globe,
  Smartphone, Monitor, Laptop, MapPin, Activity, Eye,
  CheckCircle, XCircle, AlertOctagon, Wifi, Server
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const severityConfig = {
  critical: { color: 'bg-red-100 text-red-800 border-red-200', icon: AlertTriangle, label: 'Critical' },
  warning: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: AlertCircle, label: 'Warning' },
  info: { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Info, label: 'Info' }
};

const categoryConfig = {
  security: { icon: Shield, color: 'text-red-600', label: 'Security' },
  admin: { icon: Users, color: 'text-purple-600', label: 'Admin' },
  clinic: { icon: Building2, color: 'text-blue-600', label: 'Clinic' },
  content: { icon: FileText, color: 'text-green-600', label: 'Content' },
  financial: { icon: DollarSign, color: 'text-yellow-600', label: 'Financial' },
  general: { icon: Info, color: 'text-gray-600', label: 'General' }
};

const riskLevelConfig = {
  high: { color: 'bg-red-500 text-white', label: 'High Risk' },
  medium: { color: 'bg-orange-500 text-white', label: 'Medium Risk' },
  low: { color: 'bg-yellow-500 text-white', label: 'Low Risk' },
  none: { color: 'bg-green-500 text-white', label: 'Safe' }
};

const getDeviceIcon = (deviceInfo) => {
  if (!deviceInfo) return Monitor;
  const type = deviceInfo.device_type?.toLowerCase() || '';
  if (type.includes('mobile')) return Smartphone;
  if (type.includes('tablet')) return Laptop;
  if (type.includes('bot')) return Server;
  return Monitor;
};

const EnhancedAuditLogs = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [securityOverview, setSecurityOverview] = useState(null);
  const [securityAlerts, setSecurityAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('logs');
  const [filters, setFilters] = useState({
    search: '',
    severity: '',
    category: '',
    action: '',
    country: '',
    riskLevel: '',
    startDate: '',
    endDate: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    actions: [],
    categories: [],
    countries: []
  });
  const [pagination, setPagination] = useState({
    offset: 0,
    limit: 50,
    hasMore: true
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showLogDetail, setShowLogDetail] = useState(false);
  const [ipAnalysis, setIpAnalysis] = useState(null);
  const [showIpAnalysis, setShowIpAnalysis] = useState(false);

  // Fetch all data
  const fetchData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', pagination.limit);
      params.append('offset', 0);
      
      if (filters.search) params.append('search', filters.search);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.category) params.append('category', filters.category);
      if (filters.action) params.append('action', filters.action);
      if (filters.country) params.append('country', filters.country);
      if (filters.riskLevel) params.append('risk_level', filters.riskLevel);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);

      const [logsRes, statsRes, overviewRes, alertsRes, actionsRes, categoriesRes, countriesRes] = await Promise.all([
        axios.get(`${API_URL}/api/audit/logs?${params.toString()}`),
        axios.get(`${API_URL}/api/audit/logs/stats?days=7`),
        axios.get(`${API_URL}/api/audit/dashboard/security-overview`),
        axios.get(`${API_URL}/api/audit/security-alerts?status=new&limit=10`),
        axios.get(`${API_URL}/api/audit/logs/actions`),
        axios.get(`${API_URL}/api/audit/logs/categories`),
        axios.get(`${API_URL}/api/audit/logs/countries`)
      ]);
      
      setLogs(logsRes.data);
      setStats(statsRes.data);
      setSecurityOverview(overviewRes.data);
      setSecurityAlerts(alertsRes.data);
      setFilterOptions({
        actions: actionsRes.data,
        categories: categoriesRes.data,
        countries: countriesRes.data
      });
      setPagination(prev => ({
        ...prev,
        offset: 0,
        hasMore: logsRes.data.length === pagination.limit
      }));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    fetchData();
  }, []);

  // Filter change effect
  useEffect(() => {
    const debounce = setTimeout(() => {
      const applyFilters = async () => {
        try {
          setLoading(true);
          const params = new URLSearchParams();
          params.append('limit', pagination.limit);
          params.append('offset', 0);
          
          if (filters.search) params.append('search', filters.search);
          if (filters.severity) params.append('severity', filters.severity);
          if (filters.category) params.append('category', filters.category);
          if (filters.action) params.append('action', filters.action);
          if (filters.country) params.append('country', filters.country);
          if (filters.riskLevel) params.append('risk_level', filters.riskLevel);
          if (filters.startDate) params.append('start_date', filters.startDate);
          if (filters.endDate) params.append('end_date', filters.endDate);

          const response = await axios.get(`${API_URL}/api/audit/logs?${params.toString()}`);
          setLogs(response.data);
          setPagination(prev => ({
            ...prev,
            offset: 0,
            hasMore: response.data.length === pagination.limit
          }));
        } catch (error) {
          console.error('Error applying filters:', error);
        } finally {
          setLoading(false);
        }
      };
      
      applyFilters();
    }, 300);
    return () => clearTimeout(debounce);
  }, [filters, pagination.limit]);

  const handlePageChange = async (newOffset) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('limit', pagination.limit);
      params.append('offset', newOffset);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key === 'riskLevel' ? 'risk_level' : key, value);
      });

      const response = await axios.get(`${API_URL}/api/audit/logs?${params.toString()}`);
      setLogs(response.data);
      setPagination(prev => ({
        ...prev,
        offset: newOffset,
        hasMore: response.data.length === pagination.limit
      }));
    } catch (error) {
      console.error('Error changing page:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.append('format', format);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.category) params.append('category', filters.category);
      if (filters.startDate) params.append('start_date', filters.startDate);
      if (filters.endDate) params.append('end_date', filters.endDate);

      const response = await axios.get(`${API_URL}/api/audit/logs/export?${params.toString()}`);
      
      const blob = format === 'csv' 
        ? new Blob([response.data.content], { type: 'text/csv' })
        : new Blob([JSON.stringify(response.data.content, null, 2)], { type: 'application/json' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await axios.put(`${API_URL}/api/audit/security-alerts/${alertId}/acknowledge`);
      setSecurityAlerts(prev => prev.filter(a => a.id !== alertId));
      fetchData();
    } catch (error) {
      console.error('Error acknowledging alert:', error);
    }
  };

  const handleAnalyzeIp = async (ipAddress) => {
    try {
      const response = await axios.get(`${API_URL}/api/audit/ip/${ipAddress}/analysis`);
      setIpAnalysis(response.data);
      setShowIpAnalysis(true);
    } catch (error) {
      console.error('Error analyzing IP:', error);
    }
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      severity: '',
      category: '',
      action: '',
      country: '',
      riskLevel: '',
      startDate: '',
      endDate: ''
    });
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSeverityBadge = (severity) => {
    const config = severityConfig[severity] || severityConfig.info;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} border flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getCategoryBadge = (category) => {
    const config = categoryConfig[category] || categoryConfig.general;
    const Icon = config.icon;
    return (
      <div className={`flex items-center gap-1 ${config.color}`}>
        <Icon className="h-4 w-4" />
        <span className="text-sm">{config.label}</span>
      </div>
    );
  };

  const getRiskBadge = (riskScore) => {
    let level = 'none';
    if (riskScore >= 50) level = 'high';
    else if (riskScore >= 25) level = 'medium';
    else if (riskScore > 0) level = 'low';
    
    const config = riskLevelConfig[level];
    return (
      <Badge className={config.color}>
        {riskScore || 0} - {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-7 w-7 text-teal-600" />
            Enhanced Audit Logs
          </h1>
          <p className="text-gray-600 mt-1">
            Comprehensive security logging with IP tracking, geolocation, and threat detection
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => handleExport('csv')}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button variant="outline" onClick={() => handleExport('json')}>
            <Download className="h-4 w-4 mr-2" />
            JSON
          </Button>
          <Button onClick={fetchData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Security Overview Cards */}
      {securityOverview && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card className={securityOverview.alerts.critical_count > 0 ? 'border-red-400 bg-red-50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Security Alerts</p>
                  <p className="text-2xl font-bold">{securityOverview.alerts.new_count}</p>
                  {securityOverview.alerts.critical_count > 0 && (
                    <p className="text-xs text-red-600">{securityOverview.alerts.critical_count} critical</p>
                  )}
                </div>
                <AlertOctagon className={`h-8 w-8 ${securityOverview.alerts.critical_count > 0 ? 'text-red-500' : 'text-gray-400'}`} />
              </div>
            </CardContent>
          </Card>

          <Card className={securityOverview.failed_logins.last_24h > 10 ? 'border-orange-400 bg-orange-50' : ''}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Failed Logins (24h)</p>
                  <p className="text-2xl font-bold">{securityOverview.failed_logins.last_24h}</p>
                  <p className="text-xs text-gray-500">{securityOverview.failed_logins.unique_ips} unique IPs</p>
                </div>
                <XCircle className="h-8 w-8 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">High Risk Events</p>
                  <p className="text-2xl font-bold text-red-600">{securityOverview.high_risk_events}</p>
                  <p className="text-xs text-gray-500">Last 7 days</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Suspicious IPs</p>
                  <p className="text-2xl font-bold">{securityOverview.suspicious_ips?.length || 0}</p>
                  <p className="text-xs text-gray-500">Multiple failed attempts</p>
                </div>
                <Wifi className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Top Country</p>
                  <p className="text-2xl font-bold">
                    {securityOverview.events_by_country?.[0]?.country || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500">
                    {securityOverview.events_by_country?.[0]?.count || 0} events
                  </p>
                </div>
                <Globe className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Logs (7 days)</p>
                  <p className="text-2xl font-bold">{stats.total_logs}</p>
                  <p className="text-xs text-gray-500">{stats.unique_ips} unique IPs</p>
                </div>
                <Clock className="h-8 w-8 text-gray-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-red-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-600">Critical Events</p>
                  <p className="text-2xl font-bold text-red-600">{stats.critical_count}</p>
                </div>
                <AlertTriangle className="h-8 w-8 text-red-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-yellow-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-yellow-600">Warning Events</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.warning_count}</p>
                </div>
                <AlertCircle className="h-8 w-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-blue-600">Suspicious Activities</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.suspicious_activities || 0}</p>
                </div>
                <Activity className="h-8 w-8 text-blue-400" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-xl grid-cols-3">
          <TabsTrigger value="logs" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Activity Logs
          </TabsTrigger>
          <TabsTrigger value="alerts" className="flex items-center gap-2">
            <AlertOctagon className="h-4 w-4" />
            Security Alerts
            {securityAlerts.length > 0 && (
              <Badge variant="destructive" className="ml-1">{securityAlerts.length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="analysis" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Geographic Analysis
          </TabsTrigger>
        </TabsList>

        {/* Activity Logs Tab */}
        <TabsContent value="logs" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filters
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowFilters(!showFilters)}>
                  {showFilters ? 'Hide' : 'Show'} Advanced
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search by name, email, IP, or details..."
                    className="pl-10"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  />
                </div>

                <div className="flex flex-wrap gap-3">
                  <Select 
                    value={filters.severity || "all_severities"} 
                    onValueChange={(value) => setFilters({ ...filters, severity: value === "all_severities" ? "" : value })}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Severity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_severities">All Severities</SelectItem>
                      <SelectItem value="critical">Critical</SelectItem>
                      <SelectItem value="warning">Warning</SelectItem>
                      <SelectItem value="info">Info</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select 
                    value={filters.category || "all_categories"} 
                    onValueChange={(value) => setFilters({ ...filters, category: value === "all_categories" ? "" : value })}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_categories">All Categories</SelectItem>
                      {filterOptions.categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select 
                    value={filters.riskLevel || "all_risk"} 
                    onValueChange={(value) => setFilters({ ...filters, riskLevel: value === "all_risk" ? "" : value })}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Risk Level" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_risk">All Risk Levels</SelectItem>
                      <SelectItem value="high">High Risk</SelectItem>
                      <SelectItem value="medium">Medium Risk</SelectItem>
                      <SelectItem value="low">Low Risk</SelectItem>
                      <SelectItem value="none">No Risk</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select 
                    value={filters.country || "all_countries"} 
                    onValueChange={(value) => setFilters({ ...filters, country: value === "all_countries" ? "" : value })}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all_countries">All Countries</SelectItem>
                      {filterOptions.countries.map((country) => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  {Object.values(filters).some(v => v) && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="h-4 w-4 mr-1" />
                      Clear
                    </Button>
                  )}
                </div>

                {showFilters && (
                  <div className="flex gap-3 pt-2 border-t">
                    <div className="flex-1">
                      <label className="text-sm text-gray-600 block mb-1">Start Date</label>
                      <Input
                        type="date"
                        value={filters.startDate}
                        onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-sm text-gray-600 block mb-1">End Date</label>
                      <Input
                        type="date"
                        value={filters.endDate}
                        onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                      />
                    </div>
                    <div className="flex-1">
                      <label className="text-sm text-gray-600 block mb-1">Action</label>
                      <Select 
                        value={filters.action || "all_actions"} 
                        onValueChange={(value) => setFilters({ ...filters, action: value === "all_actions" ? "" : value })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="All Actions" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all_actions">All Actions</SelectItem>
                          {filterOptions.actions.map((action) => (
                            <SelectItem key={action} value={action}>{action}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Logs Table */}
          <Card>
            <CardHeader>
              <CardTitle>Activity Log</CardTitle>
              <CardDescription>
                Showing {logs.length} entries - Click on a row to see full details
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                </div>
              ) : logs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No audit logs found matching your criteria
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[140px]">Timestamp</TableHead>
                        <TableHead>User</TableHead>
                        <TableHead>Action</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Device</TableHead>
                        <TableHead>Severity</TableHead>
                        <TableHead>Risk</TableHead>
                        <TableHead className="w-[60px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {logs.map((log) => {
                        const DeviceIcon = getDeviceIcon(log.device_info);
                        return (
                          <TableRow 
                            key={log.id} 
                            className={`cursor-pointer hover:bg-gray-50 ${log.severity === 'critical' ? 'bg-red-50' : log.risk_score >= 25 ? 'bg-yellow-50' : ''}`}
                            onClick={() => { setSelectedLog(log); setShowLogDetail(true); }}
                          >
                            <TableCell className="text-sm text-gray-600">
                              {formatTimestamp(log.timestamp)}
                            </TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium text-gray-900 text-sm">
                                  {log.user_name || 'Unknown'}
                                </p>
                                <p className="text-xs text-gray-500">{log.user_email}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <Badge variant="secondary" className="text-xs">{log.action}</Badge>
                                <p className="text-xs text-gray-500 mt-1">{log.resource_type}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              {log.geolocation ? (
                                <div className="flex items-center gap-1 text-sm">
                                  <MapPin className="h-3 w-3 text-gray-400" />
                                  <span>{log.geolocation.city}, {log.geolocation.country_code}</span>
                                </div>
                              ) : (
                                <span className="text-gray-400 text-sm">-</span>
                              )}
                              {log.ip_address && (
                                <button 
                                  className="text-xs text-blue-600 hover:underline block"
                                  onClick={(e) => { e.stopPropagation(); handleAnalyzeIp(log.ip_address); }}
                                >
                                  {log.ip_address}
                                </button>
                              )}
                            </TableCell>
                            <TableCell>
                              {log.device_info ? (
                                <div className="flex items-center gap-1 text-sm">
                                  <DeviceIcon className="h-4 w-4 text-gray-400" />
                                  <span>{log.device_info.browser || 'Unknown'}</span>
                                </div>
                              ) : (
                                <span className="text-gray-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>{getSeverityBadge(log.severity)}</TableCell>
                            <TableCell>{getRiskBadge(log.risk_score)}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm">
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>

                  {/* Pagination */}
                  <div className="flex justify-between items-center mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      Showing {pagination.offset + 1} - {pagination.offset + logs.length} entries
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={pagination.offset === 0}
                        onClick={() => handlePageChange(Math.max(0, pagination.offset - pagination.limit))}
                      >
                        <ChevronLeft className="h-4 w-4 mr-1" />
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!pagination.hasMore}
                        onClick={() => handlePageChange(pagination.offset + pagination.limit)}
                      >
                        Next
                        <ChevronRight className="h-4 w-4 ml-1" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertOctagon className="h-5 w-5 text-red-600" />
                Active Security Alerts
              </CardTitle>
              <CardDescription>
                Review and acknowledge security alerts requiring attention
              </CardDescription>
            </CardHeader>
            <CardContent>
              {securityAlerts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-2 text-green-500" />
                  <p>No active security alerts</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {securityAlerts.map((alert) => (
                    <div 
                      key={alert.id}
                      className={`p-4 rounded-lg border ${
                        alert.severity === 'critical' ? 'border-red-300 bg-red-50' :
                        alert.severity === 'warning' ? 'border-yellow-300 bg-yellow-50' :
                        'border-gray-200 bg-gray-50'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {getSeverityBadge(alert.severity)}
                            <Badge variant="outline">{alert.type}</Badge>
                          </div>
                          <p className="font-medium text-gray-900">{alert.message}</p>
                          {alert.ip_address && (
                            <button
                              className="text-sm text-blue-600 hover:underline mt-1"
                              onClick={() => handleAnalyzeIp(alert.ip_address)}
                            >
                              IP: {alert.ip_address}
                            </button>
                          )}
                          <p className="text-xs text-gray-500 mt-2">
                            {formatTimestamp(alert.created_at)}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAcknowledgeAlert(alert.id)}
                        >
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Acknowledge
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Suspicious IPs */}
          {securityOverview?.suspicious_ips?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Wifi className="h-5 w-5 text-yellow-600" />
                  Suspicious IP Addresses
                </CardTitle>
                <CardDescription>
                  IPs with multiple failed login attempts in the last 24 hours
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>IP Address</TableHead>
                      <TableHead>Failed Attempts</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {securityOverview.suspicious_ips.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell className="font-mono">{item.ip}</TableCell>
                        <TableCell>
                          <Badge variant="destructive">{item.failed_attempts} attempts</Badge>
                        </TableCell>
                        <TableCell>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleAnalyzeIp(item.ip)}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            Analyze
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Geographic Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5 text-blue-600" />
                Activity by Country
              </CardTitle>
              <CardDescription>
                Distribution of audit events by geographic location (last 7 days)
              </CardDescription>
            </CardHeader>
            <CardContent>
              {securityOverview?.events_by_country?.length > 0 ? (
                <div className="space-y-3">
                  {securityOverview.events_by_country.map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      <div className="w-32 font-medium">{item.country}</div>
                      <div className="flex-1">
                        <div className="h-6 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-teal-500 rounded-full"
                            style={{ 
                              width: `${(item.count / securityOverview.events_by_country[0].count) * 100}%` 
                            }}
                          />
                        </div>
                      </div>
                      <div className="w-20 text-right text-sm text-gray-600">
                        {item.count} events
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No geographic data available
                </div>
              )}
            </CardContent>
          </Card>

          {stats?.by_category && (
            <Card>
              <CardHeader>
                <CardTitle>Events by Category</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(stats.by_category).map(([category, count]) => (
                    <div key={category} className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        {getCategoryBadge(category)}
                      </div>
                      <p className="text-2xl font-bold">{count}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Log Detail Modal */}
      <Dialog open={showLogDetail} onOpenChange={setShowLogDetail}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Audit Log Details</DialogTitle>
            <DialogDescription>
              Complete information about this audit event
            </DialogDescription>
          </DialogHeader>
          
          {selectedLog && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="flex items-center gap-3">
                {getSeverityBadge(selectedLog.severity)}
                {getRiskBadge(selectedLog.risk_score)}
                <Badge variant="outline">{selectedLog.category}</Badge>
              </div>

              {/* User Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  User Information
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Name:</span> {selectedLog.user_name || 'Unknown'}</div>
                  <div><span className="text-gray-500">Email:</span> {selectedLog.user_email || 'Unknown'}</div>
                  <div><span className="text-gray-500">Role:</span> {selectedLog.user_role || 'Unknown'}</div>
                  <div><span className="text-gray-500">User ID:</span> {selectedLog.user_id}</div>
                </div>
              </div>

              {/* Action Info */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Action Details
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Action:</span> <Badge>{selectedLog.action}</Badge></div>
                  <div><span className="text-gray-500">Resource Type:</span> {selectedLog.resource_type}</div>
                  <div><span className="text-gray-500">Resource ID:</span> {selectedLog.resource_id}</div>
                  {selectedLog.resource_name && (
                    <div><span className="text-gray-500">Resource Name:</span> {selectedLog.resource_name}</div>
                  )}
                </div>
                {selectedLog.details && (
                  <div className="mt-2">
                    <span className="text-gray-500">Details:</span>
                    <p className="mt-1 p-2 bg-white rounded border text-sm">{selectedLog.details}</p>
                  </div>
                )}
              </div>

              {/* Location & Device Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    Location
                  </h4>
                  <div className="space-y-1 text-sm">
                    <div><span className="text-gray-500">IP:</span> 
                      <button
                        className="text-blue-600 hover:underline ml-1"
                        onClick={() => { setShowLogDetail(false); handleAnalyzeIp(selectedLog.ip_address); }}
                      >
                        {selectedLog.ip_address || 'Unknown'}
                      </button>
                    </div>
                    {selectedLog.geolocation && (
                      <>
                        <div><span className="text-gray-500">Country:</span> {selectedLog.geolocation.country}</div>
                        <div><span className="text-gray-500">City:</span> {selectedLog.geolocation.city}</div>
                        <div><span className="text-gray-500">ISP:</span> {selectedLog.geolocation.isp}</div>
                        {selectedLog.geolocation.is_proxy && (
                          <Badge variant="destructive">Proxy/VPN Detected</Badge>
                        )}
                        {selectedLog.geolocation.is_datacenter && (
                          <Badge variant="outline">Datacenter IP</Badge>
                        )}
                      </>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    <Monitor className="h-4 w-4" />
                    Device
                  </h4>
                  <div className="space-y-1 text-sm">
                    {selectedLog.device_info ? (
                      <>
                        <div><span className="text-gray-500">Browser:</span> {selectedLog.device_info.browser} {selectedLog.device_info.browser_version}</div>
                        <div><span className="text-gray-500">OS:</span> {selectedLog.device_info.os} {selectedLog.device_info.os_version}</div>
                        <div><span className="text-gray-500">Device:</span> {selectedLog.device_info.device_type}</div>
                        {selectedLog.device_info.is_bot && (
                          <Badge variant="destructive">Bot Detected</Badge>
                        )}
                      </>
                    ) : (
                      <p className="text-gray-400">No device info available</p>
                    )}
                    {selectedLog.device_fingerprint && (
                      <div><span className="text-gray-500">Fingerprint:</span> <code className="text-xs">{selectedLog.device_fingerprint}</code></div>
                    )}
                  </div>
                </div>
              </div>

              {/* Risk Alerts */}
              {selectedLog.risk_alerts && selectedLog.risk_alerts.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-4 w-4" />
                    Security Alerts
                  </h4>
                  <div className="space-y-2">
                    {selectedLog.risk_alerts.map((alert, index) => (
                      <div key={index} className="bg-white p-2 rounded border border-red-100">
                        <div className="flex items-center gap-2">
                          <Badge className={severityConfig[alert.severity]?.color || 'bg-gray-100'}>
                            {alert.severity}
                          </Badge>
                          <span className="font-medium text-sm">{alert.type}</span>
                        </div>
                        <p className="text-sm mt-1">{alert.message}</p>
                        {alert.recommendation && (
                          <p className="text-xs text-gray-500 mt-1">💡 {alert.recommendation}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Request Info */}
              {(selectedLog.request_method || selectedLog.request_path) && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Request Info</h4>
                  <div className="text-sm space-y-1">
                    {selectedLog.request_method && (
                      <div><span className="text-gray-500">Method:</span> <Badge variant="outline">{selectedLog.request_method}</Badge></div>
                    )}
                    {selectedLog.request_path && (
                      <div><span className="text-gray-500">Path:</span> <code className="text-xs bg-gray-200 px-1 rounded">{selectedLog.request_path}</code></div>
                    )}
                    {selectedLog.response_status && (
                      <div><span className="text-gray-500">Status:</span> {selectedLog.response_status}</div>
                    )}
                    {selectedLog.duration_ms && (
                      <div><span className="text-gray-500">Duration:</span> {selectedLog.duration_ms}ms</div>
                    )}
                  </div>
                </div>
              )}

              <div className="text-xs text-gray-500 pt-2 border-t">
                <div>Timestamp: {selectedLog.timestamp}</div>
                <div>Log ID: {selectedLog.id}</div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* IP Analysis Modal */}
      <Dialog open={showIpAnalysis} onOpenChange={setShowIpAnalysis}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wifi className="h-5 w-5" />
              IP Address Analysis
            </DialogTitle>
            <DialogDescription>
              Detailed analysis and activity history for this IP
            </DialogDescription>
          </DialogHeader>
          
          {ipAnalysis && (
            <div className="space-y-6">
              <div className="flex items-center gap-3">
                <code className="text-lg font-mono bg-gray-100 px-3 py-1 rounded">{ipAnalysis.ip_address}</code>
                <Badge className={riskLevelConfig[ipAnalysis.risk_assessment?.level]?.color}>
                  {ipAnalysis.risk_assessment?.level?.toUpperCase()} RISK
                </Badge>
              </div>

              {/* Geolocation */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Geolocation
                </h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">Country:</span> {ipAnalysis.geolocation?.country}</div>
                  <div><span className="text-gray-500">City:</span> {ipAnalysis.geolocation?.city}</div>
                  <div><span className="text-gray-500">Region:</span> {ipAnalysis.geolocation?.region}</div>
                  <div><span className="text-gray-500">ISP:</span> {ipAnalysis.geolocation?.isp}</div>
                  <div><span className="text-gray-500">Timezone:</span> {ipAnalysis.geolocation?.timezone}</div>
                </div>
                <div className="flex gap-2 mt-2">
                  {ipAnalysis.geolocation?.is_proxy && <Badge variant="destructive">Proxy/VPN</Badge>}
                  {ipAnalysis.geolocation?.is_datacenter && <Badge variant="outline">Datacenter</Badge>}
                </div>
              </div>

              {/* Activity Summary */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h4 className="font-semibold mb-2 flex items-center gap-2">
                  <Activity className="h-4 w-4" />
                  Activity Summary (30 days)
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Total Events</p>
                    <p className="text-2xl font-bold">{ipAnalysis.activity_summary?.total_events}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Unique Users</p>
                    <p className="text-2xl font-bold">{ipAnalysis.activity_summary?.unique_users}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Failed Logins</p>
                    <p className="text-2xl font-bold text-red-600">{ipAnalysis.activity_summary?.failed_logins}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">First Seen</p>
                    <p className="text-sm">{ipAnalysis.activity_summary?.first_seen ? formatTimestamp(ipAnalysis.activity_summary.first_seen) : 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Risk Factors */}
              {ipAnalysis.risk_assessment?.factors?.length > 0 && (
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <h4 className="font-semibold mb-2 flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-4 w-4" />
                    Risk Factors
                  </h4>
                  <ul className="list-disc list-inside space-y-1 text-sm">
                    {ipAnalysis.risk_assessment.factors.map((factor, index) => (
                      <li key={index}>{factor}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Actions Breakdown */}
              {ipAnalysis.activity_summary?.actions_breakdown && Object.keys(ipAnalysis.activity_summary.actions_breakdown).length > 0 && (
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold mb-2">Actions Breakdown</h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(ipAnalysis.activity_summary.actions_breakdown).map(([action, count]) => (
                      <Badge key={action} variant="outline">
                        {action}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedAuditLogs;
